import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Space,
  Popconfirm,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  PlusOutlined,
  StopOutlined,
  CopyOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import api from '../../utils/api';

const { Paragraph, Text } = Typography;

interface ApiTokenRow {
  id: number;
  user_id: number;
  user_email: string | null;
  name: string;
  token_prefix: string;
  rate_limit_per_min: number | null;
  scopes: string[] | null;
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
  updated_at: string;
}

interface UserOption {
  id: number;
  email: string;
  is_active: boolean;
  role: string;
}

const ApiTokensPage: React.FC = () => {
  const [tokens, setTokens] = useState<ApiTokenRow[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(20);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchTokens();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeRevoked]);

  const fetchTokens = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/api-tokens', {
        params: { include_revoked: includeRevoked },
      });
      setTokens(res.data);
    } catch (e: any) {
      message.error('Failed to load tokens: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch (e: any) {
      message.error('Failed to load users: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleCreate = () => {
    form.resetFields();
    setCreateOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: any = {
        user_id: values.user_id,
        name: values.name.trim(),
      };
      if (values.expires_in_days) payload.expires_in_days = values.expires_in_days;
      if (values.rate_limit_per_min) payload.rate_limit_per_min = values.rate_limit_per_min;

      const res = await api.post('/admin/api-tokens', payload);
      setCreatedToken(res.data.token);
      setCreateOpen(false);
      fetchTokens();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error('Create failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleRevoke = async (id: number) => {
    try {
      await api.post(`/admin/api-tokens/${id}/revoke`);
      message.success('Token revoked');
      fetchTokens();
    } catch (e: any) {
      message.error('Revoke failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success('Copied to clipboard');
    } catch {
      message.error('Failed to copy');
    }
  };

  const isExpired = (row: ApiTokenRow) =>
    !!row.expires_at && new Date(row.expires_at).getTime() < Date.now();

  const statusTag = (row: ApiTokenRow) => {
    if (row.revoked_at) return <Tag color="red">Revoked</Tag>;
    if (isExpired(row)) return <Tag color="orange">Expired</Tag>;
    return <Tag color="green">Active</Tag>;
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Prefix',
      dataIndex: 'token_prefix',
      key: 'token_prefix',
      render: (v: string) => <Text code>{v}…</Text>,
    },
    {
      title: 'Owner',
      dataIndex: 'user_email',
      key: 'user_email',
      render: (v: string | null, row: ApiTokenRow) => v || `user#${row.user_id}`,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, row: ApiTokenRow) => statusTag(row),
      width: 100,
    },
    {
      title: 'Rate Limit',
      dataIndex: 'rate_limit_per_min',
      key: 'rate_limit_per_min',
      render: (v: number | null) => (v ? `${v}/min` : '— (default)'),
    },
    {
      title: 'Expires At',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (v: string | null) => (v ? new Date(v).toLocaleString() : 'Never'),
    },
    {
      title: 'Last Used',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      render: (v: string | null) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, row: ApiTokenRow) =>
        row.revoked_at ? (
          <Text type="secondary">Revoked</Text>
        ) : (
          <Popconfirm
            title="Revoke this token? It will stop working within ~5 seconds."
            okText="Revoke"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={() => handleRevoke(row.id)}
          >
            <Button danger size="small" icon={<StopOutlined />}>
              Revoke
            </Button>
          </Popconfirm>
        ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <h1 style={{ margin: 0 }}>API Tokens</h1>
        <Space>
          <span>
            Show revoked{' '}
            <Switch
              checked={includeRevoked}
              onChange={setIncludeRevoked}
              size="small"
            />
          </span>
          <Button icon={<ReloadOutlined />} onClick={fetchTokens}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            New Token
          </Button>
        </Space>
      </div>

      <Paragraph type="secondary" style={{ marginTop: -8 }}>
        These tokens authenticate third parties calling the public read-only API
        at <Text code>/api/public/*</Text>. Tokens are bound to a system user
        account and inherit that user&apos;s data visibility.
      </Paragraph>

      <Table
        columns={columns}
        dataSource={tokens}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (_p, s) => setPageSize(s),
        }}
        scroll={{ x: 'max-content' }}
      />

      {/* Create modal */}
      <Modal
        open={createOpen}
        title="Create API Token"
        okText="Create"
        cancelText="Cancel"
        onOk={handleSubmit}
        onCancel={() => setCreateOpen(false)}
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: 'Please enter a label' },
              { max: 100 },
            ]}
          >
            <Input placeholder="e.g. Marketing dashboard – read only" />
          </Form.Item>
          <Form.Item
            name="user_id"
            label="Owner (system user)"
            rules={[{ required: true, message: 'Please pick a user' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select an active user to bind this token to"
              options={users
                .filter((u) => u.is_active)
                .map((u) => ({
                  value: u.id,
                  label: `${u.email} (${u.role})`,
                }))}
            />
          </Form.Item>
          <Form.Item
            name="expires_in_days"
            label="Expires in (days)"
            extra="Leave empty for a token that never expires."
          >
            <InputNumber min={1} max={3650} style={{ width: '100%' }} placeholder="e.g. 365" />
          </Form.Item>
          <Form.Item
            name="rate_limit_per_min"
            label="Rate limit override (req/min)"
            extra="Leave empty to use the system default (200 req/min)."
          >
            <InputNumber min={1} max={10000} style={{ width: '100%' }} placeholder="200" />
          </Form.Item>
        </Form>
      </Modal>

      {/* One-time plaintext modal */}
      <Modal
        open={!!createdToken}
        title="Copy this token now"
        footer={
          <Button type="primary" onClick={() => setCreatedToken(null)}>
            I have saved it
          </Button>
        }
        closable={false}
        maskClosable={false}
        width={620}
      >
        <Paragraph>
          This is the <Text strong>only time</Text> the plaintext token will be
          shown. Store it somewhere safe (a password manager or your secrets
          vault). If lost, revoke it and create a new one.
        </Paragraph>
        <Input.TextArea
          value={createdToken ?? ''}
          autoSize
          readOnly
          style={{ fontFamily: 'monospace' }}
        />
        <div style={{ marginTop: 12 }}>
          <Button
            icon={<CopyOutlined />}
            onClick={() => createdToken && copyToClipboard(createdToken)}
          >
            Copy to clipboard
          </Button>
        </div>
        <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
          Send it as <Text code>Authorization: Bearer &lt;token&gt;</Text> when
          calling the public API.
        </Paragraph>
      </Modal>
    </div>
  );
};

export default ApiTokensPage;
